import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FolderOpen, UserPlus } from "lucide-react";

const EmployeeDialogSection = ({ ctx }) => {
  const {
    editingEmployee,
    employeeDialogOpen,
    employeeForm,
    handleEmployeeSubmit,
    projects,
    resetEmployeeForm,
    setEditingEmployee,
    setEmployeeDialogOpen,
    setEmployeeForm
  } = ctx;
  return (
    <>
            <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
              <DialogTrigger asChild>
                <button
                  onClick={() => { setEditingEmployee(null); resetEmployeeForm(); }}
                  data-testid="add-employee-button"
                  className="btn-primary flex items-center space-x-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Add Employee</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">
                    {editingEmployee ? 'EDIT EMPLOYEE' : 'ADD NEW EMPLOYEE'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEmployeeSubmit} className="space-y-6 mt-4">
                  {/* Project Assignment - Prominent at top */}
                  {projects.length > 0 && (
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-sm">
                      <h4 className="text-sm font-semibold text-primary mb-3 uppercase tracking-widest flex items-center">
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Project Assignment
                      </h4>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Assigned Project</label>
                        <select
                          value={employeeForm.assigned_project_id || ""}
                          onChange={(e) => setEmployeeForm({...employeeForm, assigned_project_id: e.target.value})}
                          data-testid="emp-project-select"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">-- Not Assigned (Available for All Projects) --</option>
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.project_code ? `${project.project_code} - ` : ''}{project.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          Assign employee to a specific project for attendance tracking.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Basic Info */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Basic Information</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Employee Code *</label>
                        <input
                          type="text"
                          required
                          value={employeeForm.employee_code}
                          onChange={(e) => setEmployeeForm({...employeeForm, employee_code: e.target.value})}
                          data-testid="emp-code-input"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="EMP-001"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Full Name *</label>
                        <input
                          type="text"
                          required
                          value={employeeForm.name}
                          onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                          data-testid="emp-name-input"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Phone *</label>
                        <input
                          type="tel"
                          required
                          value={employeeForm.phone}
                          onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                          data-testid="emp-phone-input"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="9876543210"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Email</label>
                        <input
                          type="email"
                          value={employeeForm.email}
                          onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Address</label>
                        <input
                          type="text"
                          value={employeeForm.address}
                          onChange={(e) => setEmployeeForm({...employeeForm, address: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Full address"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Blood Group</label>
                        <select
                          value={employeeForm.blood_group}
                          onChange={(e) => setEmployeeForm({...employeeForm, blood_group: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select Blood Group</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </div>
                      <div className="col-span-3">
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">ID Card Return Address (If Lost)</label>
                        <textarea
                          value={employeeForm.id_card_return_address}
                          onChange={(e) => setEmployeeForm({...employeeForm, id_card_return_address: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Address to return ID card if found (e.g., Office address, Company name, Contact number)"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Employment Details */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Employment Details</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Designation *</label>
                        <input
                          type="text"
                          required
                          value={employeeForm.designation}
                          onChange={(e) => setEmployeeForm({...employeeForm, designation: e.target.value})}
                          data-testid="emp-designation-input"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Site Engineer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Department *</label>
                        <select
                          required
                          value={employeeForm.department}
                          onChange={(e) => setEmployeeForm({...employeeForm, department: e.target.value})}
                          data-testid="emp-department-select"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="Site">Site</option>
                          <option value="Engineering">Engineering</option>
                          <option value="Store">Store</option>
                          <option value="HR">HR</option>
                          <option value="Admin">Admin</option>
                          <option value="Accounts">Accounts</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Category *</label>
                        <select
                          required
                          value={employeeForm.category}
                          onChange={(e) => setEmployeeForm({...employeeForm, category: e.target.value})}
                          data-testid="emp-category-select"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="Skilled">Skilled</option>
                          <option value="Semi-skilled">Semi-skilled</option>
                          <option value="Unskilled">Unskilled</option>
                          <option value="Supervisor">Supervisor</option>
                          <option value="Staff">Staff</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Employment Type *</label>
                        <select
                          required
                          value={employeeForm.employment_type}
                          onChange={(e) => setEmployeeForm({...employeeForm, employment_type: e.target.value})}
                          data-testid="emp-type-select"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="Permanent">Permanent</option>
                          <option value="Contract">Contract</option>
                          <option value="Daily Wage">Daily Wage</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date of Joining *</label>
                        <input
                          type="date"
                          required
                          value={employeeForm.date_of_joining}
                          onChange={(e) => setEmployeeForm({...employeeForm, date_of_joining: e.target.value})}
                          data-testid="emp-doj-input"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Reporting To</label>
                        <input
                          type="text"
                          value={employeeForm.reporting_to}
                          onChange={(e) => setEmployeeForm({...employeeForm, reporting_to: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Manager name"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Documents & Bank Details</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Aadhar Number</label>
                        <input
                          type="text"
                          value={employeeForm.aadhar_number}
                          onChange={(e) => setEmployeeForm({...employeeForm, aadhar_number: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="XXXX XXXX XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">PAN Number</label>
                        <input
                          type="text"
                          value={employeeForm.pan_number}
                          onChange={(e) => setEmployeeForm({...employeeForm, pan_number: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="ABCDE1234F"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Bank Account</label>
                        <input
                          type="text"
                          value={employeeForm.bank_account}
                          onChange={(e) => setEmployeeForm({...employeeForm, bank_account: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="Account number"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Bank Name</label>
                        <input
                          type="text"
                          value={employeeForm.bank_name}
                          onChange={(e) => setEmployeeForm({...employeeForm, bank_name: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Bank name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">IFSC Code</label>
                        <input
                          type="text"
                          value={employeeForm.ifsc_code}
                          onChange={(e) => setEmployeeForm({...employeeForm, ifsc_code: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="IFSC code"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Emergency Contact</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contact Name</label>
                        <input
                          type="text"
                          value={employeeForm.emergency_contact_name}
                          onChange={(e) => setEmployeeForm({...employeeForm, emergency_contact_name: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Emergency contact name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contact Phone</label>
                        <input
                          type="tel"
                          value={employeeForm.emergency_contact_phone}
                          onChange={(e) => setEmployeeForm({...employeeForm, emergency_contact_phone: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="Emergency contact phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Salary Details */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Salary Details</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Basic Salary (₹)</label>
                        <input
                          type="number"
                          value={employeeForm.basic_salary}
                          onChange={(e) => setEmployeeForm({...employeeForm, basic_salary: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="Monthly salary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Daily Wage Rate (₹)</label>
                        <input
                          type="number"
                          value={employeeForm.daily_wage_rate}
                          onChange={(e) => setEmployeeForm({...employeeForm, daily_wage_rate: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="Daily rate"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Overtime Rate (₹/hr)</label>
                        <input
                          type="number"
                          value={employeeForm.overtime_rate}
                          onChange={(e) => setEmployeeForm({...employeeForm, overtime_rate: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          placeholder="OT rate"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button type="button" onClick={() => setEmployeeDialogOpen(false)} className="btn-outline">
                      Cancel
                    </button>
                    <button type="submit" data-testid="submit-employee-button" className="btn-primary">
                      {editingEmployee ? 'Update Employee' : 'Add Employee'}
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
    </>
  );
};

export default EmployeeDialogSection;
